import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import {
  Badge,
  Button,
  FormField,
  Input,
} from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { businessesApi, entitiesApi, personsApi } from "@/api/counterparties";
import { dealsApi } from "@/api/deals";
import { brokersApi } from "@/api/brokers";
import { brokerPickerLabel } from "@/lib/brokerDisplay";
import { loansApi, vendorsApi } from "@/api/portfolioAdmin/client";
import { ownerPickerKeys } from "./ownerPickerKeys";

// External Intake & Collection v1.0 PR 8 (Series 1) — entity-type +
// counterparty search widget. Used in the package list filter and the
// new-package flow so staff can attach a CollectionPackage to a Deal,
// Person, Entity, Business — plus, per External Portal v2 PR 2,
// Broker and Vendor. Soft polymorphism mirrors the backend
// OwnerEntityType (string) + OwnerEntityId (Guid).

export type OwnerEntityType =
  | "Deal"
  | "Person"
  | "Entity"
  | "Business"
  | "Broker"
  | "Vendor"
  | "Loan";

export const OWNER_TYPES: { value: OwnerEntityType; label: string }[] = [
  { value: "Deal", label: "Deal" },
  { value: "Person", label: "Person" },
  { value: "Entity", label: "Entity" },
  { value: "Business", label: "Business" },
  { value: "Broker", label: "Broker" },
  { value: "Vendor", label: "Vendor" },
  { value: "Loan", label: "Loan" },
];

export interface OwnerSelection {
  ownerEntityType: OwnerEntityType;
  ownerEntityId: string;
  ownerDisplayName: string;
}

interface Props {
  value: OwnerSelection | null;
  onChange: (next: OwnerSelection | null) => void;
  /** When set, the entity-type select is hidden and locked. */
  lockedType?: OwnerEntityType;
}

interface Hit {
  id: string;
  displayName: string;
  subtitle: string | null;
}

export function OwnerPicker({ value, onChange, lockedType }: Props) {
  const [type, setType] = useState<OwnerEntityType>(lockedType ?? value?.ownerEntityType ?? "Deal");
  const [search, setSearch] = useState("");

  const personsQuery = useQuery({
    queryKey: ownerPickerKeys.search("persons", search),
    queryFn: () => personsApi.list({ q: search || undefined, pageSize: 25 }),
    enabled: type === "Person",
  });
  const entitiesQuery = useQuery({
    queryKey: ownerPickerKeys.search("entities", search),
    queryFn: () => entitiesApi.list({ q: search || undefined, pageSize: 25 }),
    enabled: type === "Entity",
  });
  const businessesQuery = useQuery({
    queryKey: ownerPickerKeys.search("businesses", search),
    queryFn: () => businessesApi.list({ q: search || undefined, pageSize: 25 }),
    enabled: type === "Business",
  });
  const dealsQuery = useQuery({
    queryKey: ownerPickerKeys.search("deals", search),
    queryFn: () => dealsApi.list({ q: search || undefined, pageSize: 25 }),
    enabled: type === "Deal",
  });
  // PR 2 (E) — Broker and Vendor pickers. Brokers search server-side via
  // `q` (company / DBA / contact / email), so a broker past the first page
  // is findable; vendors use the registry search endpoint (returns a flat
  // array, no paging). The OwnerLabelService resolves both.
  const brokersQuery = useQuery({
    queryKey: ownerPickerKeys.search("brokers", search),
    queryFn: () => brokersApi.list({ q: search || undefined, pageSize: 25 }),
    enabled: type === "Broker",
  });
  const vendorsQuery = useQuery({
    queryKey: ownerPickerKeys.search("vendors", search),
    queryFn: () => vendorsApi.search({ search: search || undefined }),
    enabled: type === "Vendor",
  });
  const loansQuery = useQuery({
    queryKey: ownerPickerKeys.search("loans", search),
    queryFn: () => loansApi.search({ search: search || undefined }),
    enabled: type === "Loan",
  });

  const hits: Hit[] = useMemo(() => {
    if (type === "Person") {
      return (personsQuery.data?.data ?? []).map((p) => ({
        id: p.id,
        displayName: p.displayName,
        subtitle: p.primaryEmail ?? p.primaryPhone ?? p.state ?? null,
      }));
    }
    if (type === "Entity") {
      return (entitiesQuery.data?.data ?? []).map((e) => ({
        id: e.id,
        displayName: e.legalEntityName,
        subtitle: e.dba ? `DBA ${e.dba}` : (e.entityType ?? e.stateOfFormation ?? null),
      }));
    }
    if (type === "Business") {
      return (businessesQuery.data?.data ?? []).map((b) => ({
        id: b.id,
        displayName: b.legalEntityName,
        subtitle: [b.naicsCode, b.hqState].filter(Boolean).join(" · ") || null,
      }));
    }
    if (type === "Broker") {
      return (brokersQuery.data?.data ?? []).map((b) => {
        const { primary, secondary } = brokerPickerLabel(b);
        return {
          id: b.id,
          displayName: primary,
          subtitle: secondary || (b.dba ? `DBA ${b.dba}` : (b.state ?? null)),
        };
      });
    }
    if (type === "Vendor") {
      return (vendorsQuery.data ?? []).map((v) => ({
        id: v.id,
        displayName: v.name,
        subtitle: v.vendorType ?? v.status,
      }));
    }
    if (type === "Loan") {
      return (loansQuery.data ?? []).map((l) => ({
        id: l.id,
        displayName: l.loanNumber ?? l.id.slice(0, 8),
        subtitle: l.borrowerName ?? null,
      }));
    }
    return (dealsQuery.data?.data ?? []).map((d) => ({
      id: d.id,
      displayName: d.dealNumber ?? d.id.slice(0, 8),
      subtitle: d.businessNameInitial ?? `${d.stage} · $${d.purchasePrice.toLocaleString()}`,
    }));
  }, [
    type,
    personsQuery.data,
    entitiesQuery.data,
    businessesQuery.data,
    dealsQuery.data,
    brokersQuery.data,
    vendorsQuery.data,
    loansQuery.data,
  ]);

  const isLoading =
    (type === "Person" && personsQuery.isLoading) ||
    (type === "Entity" && entitiesQuery.isLoading) ||
    (type === "Business" && businessesQuery.isLoading) ||
    (type === "Deal" && dealsQuery.isLoading) ||
    (type === "Broker" && brokersQuery.isLoading) ||
    (type === "Vendor" && vendorsQuery.isLoading) ||
    (type === "Loan" && loansQuery.isLoading);

  // Once a value is set we display the selection plus a Change action,
  // so the user doesn't accidentally re-pick when they came in pre-filled.
  if (value) {
    return (
      <div className="flex items-center justify-between gap-space-3 rounded-md border border-border-light p-space-3">
        <div className="flex items-center gap-space-2 min-w-0">
          <Badge status="info">{value.ownerEntityType}</Badge>
          <span className="text-small font-semibold text-onyx truncate">
            {value.ownerDisplayName}
          </span>
        </div>
        {!lockedType && (
          <Button variant="tertiary" size="sm" onClick={() => onChange(null)}>
            Change
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-3">
      {!lockedType && (
        <FormField label="Owner type" helperText="What this document request is attached to.">
          <Select
            value={type}
            onChange={(e) => {
              setType(e.target.value as OwnerEntityType);
              setSearch("");
            }}
          >
            {OWNER_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        </FormField>
      )}

      <FormField label="Search" htmlFor="owner-search">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid pointer-events-none"
            aria-hidden
          />
          <Input
            id="owner-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${type.toLowerCase()}s…`}
            className="pl-space-5"
          />
        </div>
      </FormField>

      <div className="border border-border-light rounded-md overflow-hidden">
        {isLoading ? (
          <div className="p-space-4 text-small text-gray-mid italic">Searching…</div>
        ) : hits.length === 0 ? (
          <div className="p-space-4 text-small text-gray-mid italic">
            No matches. Try a broader search.
          </div>
        ) : (
          <ul className="divide-y divide-border-light max-h-[260px] overflow-y-auto m-0 p-0 list-none">
            {hits.map((h) => (
              <li
                key={h.id}
                className="flex items-center justify-between gap-space-3 p-space-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-small text-onyx truncate">
                    {h.displayName}
                  </div>
                  {h.subtitle && (
                    <p className="text-caption text-gray-mid mt-space-1 m-0">
                      {h.subtitle}
                    </p>
                  )}
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() =>
                    onChange({
                      ownerEntityType: type,
                      ownerEntityId: h.id,
                      ownerDisplayName: h.displayName,
                    })
                  }
                >
                  Select
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
